import React, { useState } from "react";
import { Phone, Mail, Globe, Send, CheckCircle2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { company, services } from "../../mock";
import { createLead } from "../../lib/api";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    try {
      await createLead({ ...form, source: "Website" });
      toast.success("Thank you! We'll be in touch shortly.");
      setSubmitted(true);
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
      setTimeout(() => setSubmitted(false), 4000);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  return (
    <section id="contact" className="py-20 bg-navy-deep">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12">
        <div>
          <span className="text-gold font-semibold tracking-[0.2em] uppercase text-sm">
            Get In Touch
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mt-3">
            Start Your Journey To Financial Freedom
          </h2>
          <p className="mt-4 text-gray-300 text-lg leading-relaxed">
            Book your free consultation today. Our experienced coaches are ready
            to help you build a personalized, faith-based financial plan.
          </p>

          <div className="mt-8 space-y-4">
            <a href={`tel:${company.phone}`} className="flex items-center gap-4 group">
              <div className="h-12 w-12 rounded-lg bg-navy-light flex items-center justify-center group-hover:bg-gold transition-colors">
                <Phone className="h-5 w-5 text-gold group-hover:text-navy" />
              </div>
              <div>
                <div className="text-gray-400 text-sm">Call us</div>
                <div className="text-white font-semibold">{company.phone}</div>
              </div>
            </a>
            <a href={`mailto:${company.email}`} className="flex items-center gap-4 group">
              <div className="h-12 w-12 rounded-lg bg-navy-light flex items-center justify-center group-hover:bg-gold transition-colors">
                <Mail className="h-5 w-5 text-gold group-hover:text-navy" />
              </div>
              <div>
                <div className="text-gray-400 text-sm">Email us</div>
                <div className="text-white font-semibold">{company.email}</div>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-navy-light flex items-center justify-center">
                <Globe className="h-5 w-5 text-gold" />
              </div>
              <div>
                <div className="text-gray-400 text-sm">Visit</div>
                <div className="text-white font-semibold">{company.website}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-2xl">
          {submitted ? (
            <div className="h-full min-h-[420px] flex flex-col items-center justify-center text-center">
              <CheckCircle2 className="h-16 w-16 text-green-500" />
              <h3 className="mt-4 font-serif text-2xl font-bold text-navy">
                Request Received!
              </h3>
              <p className="mt-2 text-gray-600">
                Thank you for reaching out. A coach will contact you soon.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <h3 className="font-serif text-2xl font-bold text-navy">
                Request a Free Consultation
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Full Name *</label>
                  <Input
                    value={form.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder="John Doe"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Phone</label>
                  <Input
                    value={form.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    placeholder="252-364-3138"
                    className="mt-1.5"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Email *</label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="you@email.com"
                  className="mt-1.5"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Service of Interest
                </label>
                <Select
                  value={form.service}
                  onValueChange={(v) => handleChange("service", v)}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((s) => (
                      <SelectItem key={s.id} value={s.title}>
                        {s.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Message</label>
                <Textarea
                  value={form.message}
                  onChange={(e) => handleChange("message", e.target.value)}
                  placeholder="Tell us how we can help..."
                  rows={4}
                  className="mt-1.5"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-gold hover:bg-gold-dark text-navy font-semibold h-12"
              >
                Submit Request <Send className="h-4 w-4 ml-1.5" />
              </Button>
              <p className="text-xs text-gray-400 text-center">
                By submitting, you agree to be contacted about RFL services.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
